import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { queryOptions } from '@ngneat/query';
import { useQuery, WebNotificationService } from '@sona/angular-util-common';
import {
    ByDeviceType,
    IScan,
    IScanRequestWithScore,
    ScanCategory
} from '@sona/shared-scan-model-common';
import { ITarget } from '@sona/shared-model-common';

import type { Paginated, PaginateQuery } from 'nestjs-paginate';

export type HistoricalScan = Pick<
    IScan,
    'id' | 'createdAt' | 'state' | 'score'
>;
export type ScanHistory = {
    scans: HistoricalScan[];
    latestScanWithScore: HistoricalScan;
    trend: number | undefined;
};
export type SupportedScanCategory =
    | ScanCategory.PERFORMANCE
    | ScanCategory.SECURITY
    | ScanCategory.SEO
    | ScanCategory.ACCESSIBILITY
    | ScanCategory.BEST_PRACTICES;
export type TargetHistory = Record<
    SupportedScanCategory,
    ByDeviceType<ScanHistory>
>;

/**
 * Facade for handling actions and exposing state related to the scan detail page.
 */
@Injectable({ providedIn: 'root' })
export class SonaTargetDetailFacade {
    private readonly _notificationService = inject(WebNotificationService);
    private readonly _http = inject(HttpClient);

    scan(targetId: string) {
        if(this._notificationService.isEnabled) {
            this._notificationService.requestSubscription();
        }

        return this._http.post<IScanRequestWithScore>(
            `/api/scan/request/target/${targetId}`,
            {}
        );
    }

    getTarget(targetId: string | Signal<string>) {
        return useQuery(targetId, (id) =>
            queryOptions({
                queryKey: ['targets', id] as const,
                enabled: id !== null,
                queryFn: () => this._http.get<ITarget>(`/api/targets/${id}`)
            })
        );
    }

    getHistory(targetId: string | Signal<string>) {
        return useQuery(targetId, (id) =>
            queryOptions({
                queryKey: ['targets', id, 'history'] as const,
                enabled: id !== null,
                queryFn: () =>
                    this._http.get<TargetHistory>(`/api/targets/${id}/history`)
            })
        );
    }

    getScans(
        targetId$: Signal<string>,
        query$: Signal<Omit<PaginateQuery, 'path'>>
    ) {
        return useQuery(
            computed(() => ({ targetId: targetId$(), query: query$() })),
            (options) =>
                queryOptions({
                    queryKey: [
                        'targets',
                        options?.targetId,
                        'scans',
                        options?.query
                    ] as const,
                    enabled: options?.targetId !== null,
                    queryFn: () => {
                        const params = new URLSearchParams(
                            options?.query as unknown as Record<string, string>
                        );
                        return this._http.get<Paginated<IScanRequestWithScore>>(
                            `/api/targets/${options.targetId}/scans?${params}`
                        );
                    }
                })
        );
    }
}
